import { knownIdOrNullFromInput } from "./idInput";
import type { AiPolicyMappingResult } from "./policyMapping";

export type PolicyEditorSectionText = {
  readonly heading: string;
  readonly body: string;
  readonly keywords: readonly string[];
};

export type PolicyEditorPolicyText = {
  readonly title: string;
  readonly summary: string;
  readonly tags: readonly string[];
  readonly sections: readonly PolicyEditorSectionText[];
};

export type PolicyEditorLegislationOption<Id extends string> = {
  readonly _id: Id;
  readonly title: string;
  readonly reference: string;
};

export type PolicyEditorSubscriptionRow<Legislation extends PolicyEditorLegislationOption<string>> = {
  readonly legislation: Legislation | null;
};

export type PolicyEditorSectionOption<SectionId extends string = string> = {
  readonly _id: SectionId;
};

export type PolicySectionSelectionInput<Section extends PolicyEditorSectionOption> = {
  readonly sections: readonly Section[];
  readonly activeSectionId: Section["_id"] | null;
};

export type PolicyMappingLegislationSelectionInput<Legislation extends PolicyEditorLegislationOption<string>> = {
  readonly legislation: readonly Legislation[];
  readonly selectedLegislationId: Legislation["_id"] | null;
  readonly preferredLegislationId: Legislation["_id"] | null;
};

export const policyMappingLegislationOptions = <Legislation extends PolicyEditorLegislationOption<string>>(
  subscriptions: readonly PolicyEditorSubscriptionRow<Legislation>[] | null | undefined,
): Legislation[] =>
  (subscriptions ?? [])
    .flatMap((row) => row.legislation === null ? [] : [row.legislation])
    .sort((a, b) => a.title.localeCompare(b.title));

export const selectedPolicySection = <Section extends PolicyEditorSectionOption>({
  sections,
  activeSectionId,
}: PolicySectionSelectionInput<Section>): Section | null =>
  sections.find((section) => section._id === activeSectionId) ?? sections[0] ?? null;

export const nextPolicySectionId = <Section extends PolicyEditorSectionOption>({
  sections,
  activeSectionId,
}: PolicySectionSelectionInput<Section>): Section["_id"] | null => {
  const firstSection = sections[0];
  if (firstSection === undefined) return null;
  return activeSectionId !== null && sections.some((section) => section._id === activeSectionId)
    ? activeSectionId
    : firstSection._id;
};

export const selectedPolicyMappingLegislation = <Legislation extends PolicyEditorLegislationOption<string>>({
  legislation,
  legislationId,
}: {
  readonly legislation: readonly Legislation[];
  readonly legislationId: Legislation["_id"] | null;
}): Legislation | null =>
  legislationId === null ? null : legislation.find((item) => item._id === legislationId) ?? null;

export const policyMappingLegislationIdFromInput = <Legislation extends PolicyEditorLegislationOption<string>>({
  legislation,
  value,
  fallback,
}: {
  readonly legislation: readonly Legislation[];
  readonly value: string;
  readonly fallback: Legislation["_id"] | null;
}): Legislation["_id"] | null =>
  knownIdOrNullFromInput(legislation, value, fallback);

export const nextPolicyMappingLegislationId = <Legislation extends PolicyEditorLegislationOption<string>>({
  legislation,
  selectedLegislationId,
  preferredLegislationId,
}: PolicyMappingLegislationSelectionInput<Legislation>): Legislation["_id"] | null => {
  const fallback = legislation[0]?._id ?? null;
  if (fallback === null) return null;
  return selectedLegislationId !== null && legislation.some((item) => item._id === selectedLegislationId)
    ? selectedLegislationId
    : preferredLegislationId ?? fallback;
};

export type PolicySectionSaveState = "idle" | "saving" | "saved" | "error";
export type PolicyMappingState = "idle" | "running" | "done" | "error";
export type PolicySectionDraftTone = "muted" | "saved" | "error";

export type PolicyMappingWorkflowState =
  | { readonly status: "idle"; readonly result: null; readonly error: null }
  | { readonly status: "running"; readonly result: null; readonly error: null }
  | { readonly status: "done"; readonly result: AiPolicyMappingResult; readonly error: null }
  | { readonly status: "error"; readonly result: null; readonly error: string };

export const emptyPolicyMappingWorkflowState = (): PolicyMappingWorkflowState => ({
  status: "idle",
  result: null,
  error: null,
});

export type PolicyMappingWorkflowAction =
  | { readonly type: "reset" }
  | { readonly type: "started" }
  | { readonly type: "succeeded"; readonly result: AiPolicyMappingResult }
  | { readonly type: "failed"; readonly error: string };

export const nextPolicyMappingWorkflowState = (
  _current: PolicyMappingWorkflowState,
  action: PolicyMappingWorkflowAction,
): PolicyMappingWorkflowState => {
  switch (action.type) {
    case "reset":
      return emptyPolicyMappingWorkflowState();
    case "started":
      return { status: "running", result: null, error: null };
    case "succeeded":
      return { status: "done", result: action.result, error: null };
    case "failed":
      return { status: "error", result: null, error: action.error };
  }
};

export type PolicySectionDraftState = {
  readonly body: string;
  readonly saveState: PolicySectionSaveState;
  readonly saveError: string | null;
};

export const emptyPolicySectionDraftState = (): PolicySectionDraftState => ({
  body: "",
  saveState: "idle",
  saveError: null,
});

export type PolicySectionDraftAction =
  | { readonly type: "sectionLoaded"; readonly body: string }
  | { readonly type: "bodyInput"; readonly body: string }
  | { readonly type: "saveStarted" }
  | { readonly type: "saveSucceeded" }
  | { readonly type: "saveFailed"; readonly error: string };

export const nextPolicySectionDraftState = (
  current: PolicySectionDraftState,
  action: PolicySectionDraftAction,
): PolicySectionDraftState => {
  switch (action.type) {
    case "sectionLoaded":
      return { body: action.body, saveState: "idle", saveError: null };
    case "bodyInput":
      return { ...current, body: action.body, saveState: "idle", saveError: null };
    case "saveStarted":
      return { ...current, saveState: "saving", saveError: null };
    case "saveSucceeded":
      return { ...current, saveState: "saved", saveError: null };
    case "saveFailed":
      return { ...current, saveState: "error", saveError: action.error };
  }
};

export type PolicySectionDraftView = {
  readonly changed: boolean;
  readonly canSave: boolean;
  readonly message: string;
  readonly tone: PolicySectionDraftTone;
};

const POLICY_SECTION_DRAFT_TONE_CLASSES = {
  muted: "text-slate-400",
  saved: "text-emerald-700",
  error: "text-red-600",
} satisfies Record<PolicySectionDraftTone, string>;

const POLICY_SECTION_SAVE_BUTTON_LABELS = {
  idle: "Save section",
  saving: "Saving...",
  saved: "Save section",
  error: "Save section",
} satisfies Record<PolicySectionSaveState, string>;

export const policySectionDraftToneClass = (tone: PolicySectionDraftTone): string =>
  POLICY_SECTION_DRAFT_TONE_CLASSES[tone];

export const policySectionSaveButtonLabel = (state: PolicySectionSaveState): string =>
  POLICY_SECTION_SAVE_BUTTON_LABELS[state];

export const preferredPolicyMappingLegislationId = <Id extends string>(
  policy: PolicyEditorPolicyText | null | undefined,
  legislation: readonly PolicyEditorLegislationOption<Id>[],
): Id | null => {
  if (policy == null) return null;

  const first = legislation[0];
  if (first === undefined) return null;

  const policyText = policySearchText(policy);
  const best = legislation.slice(1).reduce(
    (current, candidate) => {
      const score = legislationSearchTerms(candidate).filter((term) => policyText.includes(term)).length;
      return score > current.score ? { id: candidate._id, score } : current;
    },
    {
      id: first._id,
      score: legislationSearchTerms(first).filter((term) => policyText.includes(term)).length,
    },
  );

  return best.id;
};

export const policySectionDraftView = (input: {
  readonly hasCompany: boolean;
  readonly savedBody: string | null;
  readonly draftBody: string;
  readonly saveState: PolicySectionSaveState;
  readonly saveError: string | null;
}): PolicySectionDraftView => {
  const changed = input.savedBody !== null && input.draftBody !== input.savedBody;
  const canSave =
    input.hasCompany &&
    input.savedBody !== null &&
    changed &&
    input.draftBody.trim() !== "" &&
    input.saveState !== "saving";

  if (input.saveState === "saved") {
    return { changed, canSave, message: "Document section saved.", tone: "saved" };
  }
  if (input.saveState === "error") {
    return {
      changed,
      canSave,
      message: input.saveError ?? "Could not save document section.",
      tone: "error",
    };
  }

  return {
    changed,
    canSave,
    message: changed ? "Unsaved document wording changes." : "No wording changes yet.",
    tone: "muted",
  };
};

export const canRunPolicyMapping = (input: {
  readonly hasCompany: boolean;
  readonly hasPolicy: boolean;
  readonly selectedLegislationId: string | null;
  readonly sectionCount: number;
  readonly draftChanged: boolean;
  readonly mappingState: PolicyMappingState;
}): boolean =>
  input.hasCompany &&
  input.hasPolicy &&
  input.selectedLegislationId !== null &&
  input.sectionCount > 0 &&
  !input.draftChanged &&
  input.mappingState !== "running";

const policySearchText = (policy: PolicyEditorPolicyText): string =>
  [
    policy.title,
    policy.summary,
    ...policy.tags,
    ...policy.sections.map((section) =>
      section.heading + " " + section.body + " " + section.keywords.join(" "),
    ),
  ].join(" ").toLowerCase();

const legislationSearchTerms = <Id extends string>(
  legislation: PolicyEditorLegislationOption<Id>,
): string[] =>
  (legislation.title + " " + legislation.reference)
    .toLowerCase()
    .split(/\W+/)
    .filter((term) => term.length > 3);
